import { useMemo } from 'react';
import { User, ProjectUser, ApiKey as ProjectApiKey } from '../utils/openai-api';
import { Status, UI_CONSTANTS } from '../constants/ui';
import { isActive } from '../utils/dateUtils';

export interface ProcessedUser {
  name: string;
  email: string;
  role: string;
  projectCount: number;
  apiKeyCount: number;
  status: Status;
  projects: string[];
  lastUsed?: string;
  totalRequests: number;
  cost: number;
}

interface ProjectDetail {
  project: { id: string; name: string };
  users: ProjectUser[];
  apiKeys: ProjectApiKey[];
}

interface UseProcessedUsersProps {
  users: User[];
  projectDetails: ProjectDetail[];
}

export const useProcessedUsers = ({ users, projectDetails }: UseProcessedUsersProps): ProcessedUser[] => {
  return useMemo(() => {
    // Pre-compute user mappings for better performance
    const userProjectsMap = new Map<string, ProjectDetail[]>();
    const userApiKeysMap = new Map<string, { count: number; timestamps: number[] }>();
    
    // Single pass through projectDetails to build lookup maps
    projectDetails.forEach(detail => {
      // Map users to projects
      detail.users.forEach(pu => {
        if (!userProjectsMap.has(pu.id)) {
          userProjectsMap.set(pu.id, []);
        }
        userProjectsMap.get(pu.id)!.push(detail);
      });
      
      // Map users to API keys and timestamps
      detail.apiKeys.forEach(key => {
        if (key.owner?.type === 'user' && key.owner.user?.id) {
          const userId = key.owner.user.id;
          if (!userApiKeysMap.has(userId)) {
            userApiKeysMap.set(userId, { count: 0, timestamps: [] });
          }
          const userData = userApiKeysMap.get(userId)!;
          userData.count++;
          if (key.last_used_at) {
            userData.timestamps.push(key.last_used_at);
          }
        }
      });
    });

    return users.map(user => {
      // Get pre-computed data
      const userProjects = userProjectsMap.get(user.id) || [];
      const userApiKeyData = userApiKeysMap.get(user.id) || { count: 0, timestamps: [] };
      
      // Calculate last used date efficiently
      let lastUsed: string | undefined;
      let maxTimestamp = 0;
      
      if (userApiKeyData.timestamps.length > 0) {
        // Use reduce instead of Math.max with spread operator to avoid stack overflow
        maxTimestamp = userApiKeyData.timestamps.reduce((max, timestamp) => 
          timestamp > max ? timestamp : max, 0
        );
        lastUsed = new Date(maxTimestamp * 1000).toISOString().split('T')[0];
      }

      // Determine status using utility function
      const status: Status = maxTimestamp > 0 && 
        isActive(maxTimestamp, UI_CONSTANTS.ACTIVE_THRESHOLD_DAYS) 
        ? 'Active' 
        : 'Inactive';

      return {
        name: user.name,
        email: user.email,
        role: user.role,
        projectCount: userProjects.length,
        apiKeyCount: userApiKeyData.count,
        status,
        projects: userProjects.map(p => p.project.name),
        lastUsed,
        totalRequests: 0, // Not available via Management API
        cost: 0 // Not available via Management API
      };
    });
  }, [users, projectDetails]);
};